// apps/web/src/pages/Admin/UserCoinManager.jsx
// Support tooling inside the admin user detail panel: see every coin a user
// holds, give them a title coin or a collection coin (optionally backdated to a
// month/year), and remove coins again. Also the quickest way to see a new
// collection coin — including an animated Signature coin — on a real account.
import React, { useCallback, useEffect, useState } from 'react';
import adminApi from '../../services/adminApi';
import CoinMedia, { SignatureBadge } from '../../components/CoinMedia/CoinMedia';
import styles from './Users.module.css';

const RARITIES = ['bronze', 'silver', 'gold', 'legendary'];

function currentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonth(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

const EMPTY_FORM = {
    type: 'collection',
    collection_id: '',
    tmdb_id: '',
    kind: 'movie',
    season_number: '',
    rarity: 'gold',
    earned_month: '',
    // Explicit, audited bypass of a seasonal earning window (super admin only).
    override: false,
    override_reason: '',
};

const OVERRIDE_REASON_MIN = 10;
const SEASON_STATUS_TEXT = { upcoming: 'season not started', ended: 'season ended' };

/** Seasonal collection whose reward can't normally be earned right now. */
function outsideWindow(collection) {
    return Boolean(collection?.isSeasonal && SEASON_STATUS_TEXT[collection.availabilityStatus]);
}

export default function UserCoinManager({ userId, disabled = false, onChanged }) {
    const [coins, setCoins]             = useState(null);
    const [collections, setCollections] = useState([]);
    const [form, setForm]               = useState(EMPTY_FORM);
    const [busy, setBusy]               = useState(null); // 'grant' | coin id being removed
    const [error, setError]             = useState(null);
    const [notice, setNotice]           = useState(null);
    const [preview, setPreview]         = useState(null); // coin shown large (Signature coins animate here)

    const load = useCallback(async () => {
        try {
            const data = await adminApi.listUserCoins(userId);
            setCoins(data.coins);
        } catch (err) {
            setError(err.message || 'Failed to load coins.');
        }
    }, [userId]);

    useEffect(() => { setCoins(null); setPreview(null); load(); }, [load]);

    useEffect(() => {
        adminApi.listCollections({ limit: 200 })
            .then((data) => setCollections(data.collections || []))
            .catch(() => setCollections([]));
    }, []);

    function set(key, value) { setForm((prev) => ({ ...prev, [key]: value })); }

    const selectedCollection = collections.find((c) => c.id === form.collection_id) || null;
    const needsOverride = form.type === 'collection' && outsideWindow(selectedCollection);

    async function handleGrant(e) {
        e.preventDefault();
        setError(null);
        setNotice(null);

        const payload = { type: form.type };
        if (form.earned_month) payload.earned_month = form.earned_month;
        if (form.type === 'collection') {
            if (!form.collection_id) { setError('Choose a collection.'); return; }
            payload.collection_id = form.collection_id;
            if (form.override) {
                const reason = form.override_reason.trim();
                if (reason.length < OVERRIDE_REASON_MIN) {
                    setError(`Give a reason (at least ${OVERRIDE_REASON_MIN} characters) for granting outside the earning window.`);
                    return;
                }
                const name = selectedCollection?.name || 'this collection';
                if (!window.confirm(
                    `You are BYPASSING normal eligibility: "${name}" can't be earned right now`
                    + ` (${SEASON_STATUS_TEXT[selectedCollection?.availabilityStatus] || 'outside its window'}).\n\n`
                    + 'This grant is recorded in the audit log with your reason. Continue?'
                )) return;
                payload.override = { reason, confirm: true };
            }
        } else {
            if (!form.tmdb_id) { setError('Enter a TMDB ID.'); return; }
            payload.tmdb_id = Number(form.tmdb_id);
            payload.kind = form.kind;
            payload.rarity = form.rarity;
            if (form.kind === 'tv') {
                if (form.season_number === '') { setError('Enter a season number for TV coins.'); return; }
                payload.season_number = Number(form.season_number);
            }
        }

        setBusy('grant');
        try {
            const { coin } = await adminApi.grantUserCoin(userId, payload);
            setNotice(`Gave "${coin.title}" (${coin.sourceType === 'collection' ? 'collection' : coin.rarity}).`);
            if (payload.override) setNotice(`Gave "${coin.title}" OUTSIDE its earning window (override recorded in the audit log).`);
            setForm((prev) => ({ ...EMPTY_FORM, type: prev.type, earned_month: prev.earned_month }));
            await load();
            onChanged?.();
        } catch (err) {
            // collection_not_available: the normal grant respects the seasonal window.
            setError(err.message || 'Failed to give coin.');
        } finally {
            setBusy(null);
        }
    }

    async function handleRevoke(coin) {
        const extra = coin.sourceType === 'collection' ? ' This also clears their completion of the collection.' : '';
        if (!window.confirm(`Remove "${coin.title}" from this user?${extra}`)) return;
        setError(null);
        setNotice(null);
        setBusy(coin.id);
        try {
            await adminApi.revokeUserCoin(userId, coin.id);
            setNotice(`Removed "${coin.title}".`);
            if (preview?.id === coin.id) setPreview(null);
            await load();
            onChanged?.();
        } catch (err) {
            setError(err.message || 'Failed to remove coin.');
        } finally {
            setBusy(null);
        }
    }

    const ownedCollectionIds = new Set((coins || []).filter((c) => c.collectionId).map((c) => c.collectionId));

    return (
        <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Coins &amp; Collections {coins ? `(${coins.length})` : ''}</h3>

            {error  && <div className={styles.emailError} role="alert">{error}</div>}
            {notice && <div className={styles.coinNotice}>{notice}</div>}

            {/* ── Give a coin ── */}
            <form className={styles.coinGrantForm} onSubmit={handleGrant}>
                <label className={styles.coinField}>
                    <span>Give</span>
                    <select className={styles.select} value={form.type} onChange={(e) => set('type', e.target.value)} disabled={disabled}>
                        <option value="collection">Collection coin</option>
                        <option value="media">Title coin</option>
                    </select>
                </label>

                {form.type === 'collection' ? (
                    <label className={`${styles.coinField} ${styles.coinFieldWide}`}>
                        <span>Collection</span>
                        <select
                            className={styles.select}
                            value={form.collection_id}
                            onChange={(e) => setForm((prev) => ({ ...prev, collection_id: e.target.value, override: false, override_reason: '' }))}
                            disabled={disabled}
                        >
                            <option value="">Choose…</option>
                            {collections.map((c) => (
                                <option key={c.id} value={c.id} disabled={ownedCollectionIds.has(c.id)}>
                                    {c.name}{c.reward_media_type === 'ANIMATED' ? ' 💎' : ''}{!c.active ? ' (inactive)' : ''}
                                    {outsideWindow(c) ? ` (${SEASON_STATUS_TEXT[c.availabilityStatus]})` : ''}
                                    {ownedCollectionIds.has(c.id) ? ' — already has' : ''}
                                </option>
                            ))}
                        </select>
                    </label>
                ) : (
                    <>
                        <label className={styles.coinField}>
                            <span>TMDB ID</span>
                            <input className={styles.coinInput} type="number" min="1" value={form.tmdb_id} onChange={(e) => set('tmdb_id', e.target.value)} disabled={disabled} />
                        </label>
                        <label className={styles.coinField}>
                            <span>Kind</span>
                            <select className={styles.select} value={form.kind} onChange={(e) => set('kind', e.target.value)} disabled={disabled}>
                                <option value="movie">Movie</option>
                                <option value="tv">TV season</option>
                            </select>
                        </label>
                        {form.kind === 'tv' && (
                            <label className={styles.coinField}>
                                <span>Season</span>
                                <input className={styles.coinInput} type="number" min="0" value={form.season_number} onChange={(e) => set('season_number', e.target.value)} disabled={disabled} />
                            </label>
                        )}
                        <label className={styles.coinField}>
                            <span>Tier</span>
                            <select className={styles.select} value={form.rarity} onChange={(e) => set('rarity', e.target.value)} disabled={disabled}>
                                {RARITIES.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </label>
                    </>
                )}

                <label className={styles.coinField}>
                    <span>Earned (month / year)</span>
                    <input
                        className={styles.coinInput}
                        type="month"
                        max={currentMonth()}
                        value={form.earned_month}
                        onChange={(e) => set('earned_month', e.target.value)}
                        disabled={disabled}
                        aria-label="Earned month and year"
                    />
                </label>

                <button
                    type="submit"
                    className={styles.sendEmailBtn}
                    disabled={disabled || busy === 'grant' || (needsOverride && !form.override)}
                >
                    {busy === 'grant' ? 'Giving…' : form.override ? '⚠️ Grant outside window' : 'Give coin'}
                </button>
            </form>

            {needsOverride && (
                <div className={styles.overrideBox} role="group" aria-label="Seasonal override">
                    <p>
                        <strong>⚠️ “{selectedCollection.name}” can’t be earned right now</strong>
                        {' '}({SEASON_STATUS_TEXT[selectedCollection.availabilityStatus]}). Normal grants respect the seasonal
                        window. Only bypass it when the user qualified in-season but an Aluvo error prevented the reward.
                        Super admins only; recorded in the audit log.
                    </p>
                    <label className={styles.toggleRow}>
                        <input
                            type="checkbox"
                            checked={form.override}
                            onChange={(e) => set('override', e.target.checked)}
                            disabled={disabled}
                        />
                        <span>Grant outside earning window (bypass normal eligibility)</span>
                    </label>
                    {form.override && (
                        <label className={styles.coinField}>
                            <span>Reason (required)</span>
                            <textarea
                                className={styles.coinInput}
                                rows={2}
                                maxLength={500}
                                placeholder="e.g. Completed Oct 30; quiz submit failed (ticket #1234)"
                                value={form.override_reason}
                                onChange={(e) => set('override_reason', e.target.value)}
                                disabled={disabled}
                                aria-label="Override reason"
                            />
                        </label>
                    )}
                </div>
            )}
            <p className={styles.coinHint}>
                Leave the month empty for today. Granted coins are rendered and stored exactly like earned ones; no completion email is sent.
            </p>

            {/* ── Large preview (Signature coins animate inside their ring here) ── */}
            {preview && (
                <div className={styles.coinPreviewPanel}>
                    <CoinMedia coin={preview} animate alt={preview.title} className={styles.coinPreviewLarge} fallback={<span>🪙</span>} />
                    <div>
                        <strong>{preview.title}</strong>
                        <div><SignatureBadge coin={preview} /></div>
                        <button type="button" className={styles.linkBtn} onClick={() => setPreview(null)}>Close preview</button>
                    </div>
                </div>
            )}

            {/* ── Coins held ── */}
            {coins === null ? (
                <p className={styles.coinHint}>Loading coins…</p>
            ) : coins.length === 0 ? (
                <p className={styles.coinHint}>This user has no coins yet.</p>
            ) : (
                <div className={styles.itemsList}>
                    {coins.map((coin) => (
                        <div key={coin.id} className={styles.item} data-testid={`coin-row-${coin.id}`}>
                            <button type="button" className={styles.coinRowMain} onClick={() => setPreview(coin)} title="Preview">
                                {/* Static thumbnail only — never autoplay in a list */}
                                <CoinMedia coin={coin} alt="" className={styles.coinRowThumb} fallback={<span className={styles.coinRowThumb}>🪙</span>} />
                                <span>
                                    {coin.title}{coin.seasonNumber != null ? ` · S${coin.seasonNumber}` : ''}
                                    <SignatureBadge coin={coin} compact />
                                </span>
                            </button>
                            <span className={styles.itemBadge}>{coin.sourceType === 'collection' ? 'collection' : coin.rarity}</span>
                            <span className={styles.itemDate}>{formatMonth(coin.earnedAt)}</span>
                            <button
                                type="button"
                                className={styles.coinRemoveBtn}
                                disabled={disabled || busy === coin.id}
                                onClick={() => handleRevoke(coin)}
                            >
                                {busy === coin.id ? 'Removing…' : 'Remove'}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}
